/**
 * Cálculo PURO del punto de REANUDACIÓN de un vídeo (F3-F). Al montar el player
 * se lee el `video_watch_progress` local (posición + completado) y se decide a
 * qué segundo saltar, evitando reanudar justo al final o re-mostrar un vídeo ya
 * completado desde el último segundo.
 */

export interface ResumeInput {
  /** Posición guardada (segundos), o null si no hay progreso. */
  positionSeconds: number | null | undefined;
  /** Duración del vídeo (segundos), 0/desconocida si aún no hay metadata. */
  durationSeconds: number | null | undefined;
  /** ¿El progreso está marcado como completado? */
  completed?: boolean;
}

/** Margen (segundos) del final dentro del cual NO tiene sentido reanudar. */
const END_MARGIN_SECONDS = 5;
/** Margen (segundos) del inicio por debajo del cual se arranca en 0. */
const START_MARGIN_SECONDS = 3;

/**
 * Segundo al que saltar al abrir el player, o `null` para arrancar desde el
 * inicio (sin `seek`).
 *
 *  - Sin posición o posición ínfima → `null` (empieza en 0).
 *  - Vídeo completado → `null` (re-ver desde el principio).
 *  - Posición dentro del margen final → `null` (evita reanudar en los créditos).
 *  - En otro caso → la posición guardada (acotada a la duración conocida).
 */
export function computeResumeSeek(input: ResumeInput): number | null {
  const pos = input.positionSeconds ?? 0;
  if (!Number.isFinite(pos) || pos <= START_MARGIN_SECONDS) {
    return null;
  }
  if (input.completed) {
    return null;
  }
  const duration = input.durationSeconds ?? 0;
  if (Number.isFinite(duration) && duration > 0) {
    if (pos >= duration - END_MARGIN_SECONDS) {
      return null;
    }
    return Math.min(pos, duration - END_MARGIN_SECONDS);
  }
  return pos;
}

/** Formatea segundos a `m:ss` / `h:mm:ss` (etiquetas de tiempo del player). */
export function formatTime(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) {
    return "0:00";
  }
  const s = Math.floor(totalSeconds % 60);
  const m = Math.floor((totalSeconds / 60) % 60);
  const h = Math.floor(totalSeconds / 3600);
  const ss = s.toString().padStart(2, "0");
  if (h > 0) {
    const mm = m.toString().padStart(2, "0");
    return `${h}:${mm}:${ss}`;
  }
  return `${m}:${ss}`;
}
